/**
 * NewsletterSignup - Email capture component for blog
 *
 * Features:
 * - Free/Supporter/Professional tiers
 * - Email validation
 * - Success/error states
 * - Integrates with /api/newsletter/subscribe
 */

import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Mail, Check, Loader2, Sparkles, Zap, Crown } from 'lucide-react'
import { publicFetch } from '@/services/api'

interface NewsletterSignupProps {
  variant?: 'inline' | 'hero' | 'compact'
  source?: string
}

type Tier = 'free' | 'supporter' | 'professional'

interface TierOption {
  id: Tier
  name: string
  price: string
  description: string
  icon: React.ReactNode
  color: string
  features: string[]
}

const tiers: TierOption[] = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    description: 'Weekly newsletter',
    icon: <Mail size={20} />,
    color: '#6366f1',
    features: ['Weekly AI insights', 'New article notifications'],
  },
  {
    id: 'supporter',
    name: 'Supporter',
    price: '$5/mo',
    description: 'Monthly deep-dives',
    icon: <Zap size={20} />,
    color: '#22c55e',
    features: ['Everything in Free', 'Monthly deep-dive reports', 'Early access to articles'],
  },
  {
    id: 'professional',
    name: 'Professional',
    price: '$19/mo',
    description: 'Full access',
    icon: <Crown size={20} />,
    color: '#f59e0b',
    features: ['Everything in Supporter', 'Private Slack channel', 'Quarterly trend briefings'],
  },
]

export function NewsletterSignup({ variant = 'inline', source = 'blog' }: NewsletterSignupProps) {
  const [email, setEmail] = useState('')
  const [selectedTier, setSelectedTier] = useState<Tier>('free')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [message, setMessage] = useState('')
  const [showTiers, setShowTiers] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email || !email.includes('@')) {
      setStatus('error')
      setMessage('Please enter a valid email address')
      return
    }

    setStatus('loading')

    try {
      const response = await publicFetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          tier: selectedTier,
          source,
        }),
      })

      const data = await response.json()

      if (data.success) {
        setStatus('success')
        setMessage(data.message || 'Thanks for subscribing!')

        // Redirect to Stripe checkout for paid tiers
        if (data.checkout_url) {
          window.location.href = data.checkout_url
        }
      } else {
        setStatus('error')
        setMessage(data.error || 'Something went wrong')
      }
    } catch {
      setStatus('error')
      setMessage('Failed to subscribe. Please try again.')
    }
  }

  // Compact variant - just email input
  if (variant === 'compact') {
    return (
      <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem', width: '100%' }}>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          disabled={status === 'loading' || status === 'success'}
          style={{
            flex: 1,
            padding: '0.75rem 1rem',
            background: '#fafafa',
            border: '1px solid #eaeaea',
            borderRadius: '8px',
            color: '#0f0f0f',
            fontSize: '0.9rem',
            outline: 'none',
          }}
        />
        <motion.button
          type="submit"
          disabled={status === 'loading' || status === 'success'}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          style={{
            padding: '0.75rem 1.5rem',
            background: status === 'success' ? '#15b87f' : '#111111',
            border: 'none',
            borderRadius: '8px',
            color: '#ffffff',
            fontWeight: 600,
            cursor: status === 'loading' ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          {status === 'loading' && <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />}
          {status === 'success' && <Check size={18} />}
          {status === 'loading' ? '' : status === 'success' ? 'Subscribed!' : 'Subscribe'}
        </motion.button>
      </form>
    )
  }

  // Inline variant - email + tier selection
  if (variant === 'inline') {
    return (
      <div style={{
        padding: '1.5rem',
        background: '#fafafa',
        border: '1px solid #eaeaea',
        borderRadius: '16px',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '1rem',
        }}>
          <Sparkles size={20} color="#6366f1" />
          <span style={{ color: '#0f0f0f', fontWeight: 600 }}>The Human+AI Project</span>
        </div>

        <p style={{
          color: '#9ca3af',
          fontSize: '0.9rem',
          marginBottom: '1rem',
        }}>
          Weekly insights on thriving alongside AI. Tools, trends, and what it means to be human in an AI world.
        </p>

        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              style={{
                padding: '1rem',
                background: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)',
                borderRadius: '8px',
                color: '#22c55e',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}
            >
              <Check size={20} />
              {message}
            </motion.div>
          ) : (
            <motion.form key="form" onSubmit={handleSubmit}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  disabled={status === 'loading'}
                  style={{
                    flex: 1,
                    padding: '0.75rem 1rem',
                    background: '#fafafa',
                    border: '1px solid #eaeaea',
                    borderRadius: '8px',
                    color: '#0f0f0f',
                    fontSize: '0.9rem',
                    outline: 'none',
                  }}
                />
                <motion.button
                  type="submit"
                  disabled={status === 'loading'}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: '0.75rem 1.5rem',
                    background: '#111111',
                    border: 'none',
                    borderRadius: '8px',
                    color: '#ffffff',
                    fontWeight: 600,
                    cursor: status === 'loading' ? 'wait' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}
                >
                  {status === 'loading' ? (
                    <Loader2 size={18} style={{ animation: 'spin 1s linear infinite' }} />
                  ) : (
                    'Subscribe'
                  )}
                </motion.button>
              </div>

              {/* Tier toggle */}
              <button
                type="button"
                onClick={() => setShowTiers(!showTiers)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6366f1',
                  fontSize: '0.8rem',
                  cursor: 'pointer',
                  padding: 0,
                }}
              >
                {showTiers ? 'Hide premium tiers' : 'View premium tiers'}
              </button>

              {/* Tier selection */}
              <AnimatePresence>
                {showTiers && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3, 1fr)',
                      gap: '0.5rem',
                      marginTop: '1rem',
                    }}
                  >
                    {tiers.map((tier) => (
                      <motion.button
                        key={tier.id}
                        type="button"
                        onClick={() => setSelectedTier(tier.id)}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        style={{
                          padding: '0.75rem',
                          background: selectedTier === tier.id
                            ? `rgba(${tier.id === 'free' ? '99, 102, 241' : tier.id === 'supporter' ? '34, 197, 94' : '245, 158, 11'}, 0.2)`
                            : '#fafafa',
                          border: `1px solid ${selectedTier === tier.id ? tier.color : '#eaeaea'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          textAlign: 'left' as const,
                        }}
                      >
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '0.5rem',
                          marginBottom: '0.25rem',
                        }}>
                          <span style={{ color: tier.color }}>{tier.icon}</span>
                          <span style={{ color: '#0f0f0f', fontWeight: 600, fontSize: '0.85rem' }}>{tier.name}</span>
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{tier.price}</div>
                      </motion.button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Error message */}
              {status === 'error' && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.5rem',
                  background: 'rgba(239, 68, 68, 0.1)',
                  borderRadius: '6px',
                  color: '#ef4444',
                  fontSize: '0.85rem',
                }}>
                  {message}
                </div>
              )}
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    )
  }

  // Hero variant - full featured with tier cards
  return (
    <div style={{
      padding: '2rem',
      background: '#fafafa',
      border: '1px solid #eaeaea',
      borderRadius: '24px',
      marginBottom: '2rem',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.5rem',
          marginBottom: '0.75rem',
        }}>
          <Sparkles size={24} color="#6366f1" />
          <h2 style={{ color: '#0f0f0f', fontSize: '1.5rem', fontWeight: 700, margin: 0 }}>
            The Human+AI Project
          </h2>
        </div>
        <p style={{ color: '#9ca3af', margin: 0 }}>
          Insights and tools for thriving in an AI-powered world
        </p>
      </div>

      <AnimatePresence mode="wait">
        {status === 'success' ? (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            style={{
              padding: '2rem',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: '16px',
              textAlign: 'center',
            }}
          >
            <Check size={48} color="#22c55e" style={{ marginBottom: '1rem' }} />
            <h3 style={{ color: '#22c55e', marginBottom: '0.5rem' }}>You're In!</h3>
            <p style={{ color: '#9ca3af' }}>{message}</p>
          </motion.div>
        ) : (
          <motion.div key="form">
            {/* Tier cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '1.5rem',
            }}>
              {tiers.map((tier) => (
                <motion.button
                  key={tier.id}
                  type="button"
                  onClick={() => setSelectedTier(tier.id)}
                  whileHover={{ scale: 1.02, y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  style={{
                    padding: '1.25rem',
                    background: selectedTier === tier.id
                      ? `rgba(${tier.id === 'free' ? '99, 102, 241' : tier.id === 'supporter' ? '34, 197, 94' : '245, 158, 11'}, 0.15)`
                      : '#fafafa',
                    border: `2px solid ${selectedTier === tier.id ? tier.color : '#eaeaea'}`,
                    borderRadius: '16px',
                    cursor: 'pointer',
                    textAlign: 'left' as const,
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '0.5rem',
                  }}>
                    <span style={{ color: tier.color }}>{tier.icon}</span>
                    <span style={{ color: '#0f0f0f', fontWeight: 600 }}>{tier.name}</span>
                  </div>
                  <div style={{ color: '#0f0f0f', fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.25rem' }}>
                    {tier.price}
                  </div>
                  <div style={{ color: '#9ca3af', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
                    {tier.description}
                  </div>
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                    {tier.features.map((feature, i) => (
                      <li key={i} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#9ca3af',
                        fontSize: '0.8rem',
                        marginBottom: '0.25rem',
                      }}>
                        <Check size={12} color={tier.color} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </motion.button>
              ))}
            </div>

            {/* Email form */}
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '0.5rem' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                disabled={status === 'loading'}
                style={{
                  flex: 1,
                  padding: '1rem 1.25rem',
                  background: '#ffffff',
                  border: '1px solid #eaeaea',
                  borderRadius: '12px',
                  color: '#0f0f0f',
                  fontSize: '1rem',
                  outline: 'none',
                }}
              />
              <motion.button
                type="submit"
                disabled={status === 'loading'}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  padding: '1rem 2rem',
                  background: '#111111',
                  border: 'none',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: status === 'loading' ? 'wait' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  
                }}
              >
                {status === 'loading' ? (
                  <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                ) : (
                  <>
                    Subscribe
                    {selectedTier !== 'free' && ` · ${tiers.find(t => t.id === selectedTier)?.price}`}
                  </>
                )}
              </motion.button>
            </form>

            {/* Error message */}
            {status === 'error' && (
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                color: '#ef4444',
                fontSize: '0.9rem',
                textAlign: 'center',
              }}>
                {message}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
